import type {
  BollingerBandsBasisType,
  BollingerBandsSource,
  Candle,
} from '../types';

const DEFAULT_LENGTH = 20;
const DEFAULT_MULTIPLIER = 2;
const DEFAULT_SOURCE: BollingerBandsSource = 'close';
const DEFAULT_BASIS_TYPE: BollingerBandsBasisType = 'sma';
const DEFAULT_OFFSET = 0;

export interface BollingerBandsOverlayResult {
  upper: (number | undefined)[];
  basis: (number | undefined)[];
  lower: (number | undefined)[];
}

interface ComputeBollingerBandsParams {
  candles: Candle[];
  length?: number;
  multiplier?: number;
  source?: BollingerBandsSource;
  basisType?: BollingerBandsBasisType;
  offset?: number;
}

function toSourceValue(candle: Candle, source: BollingerBandsSource): number {
  if (source === 'open') {
    return candle.open;
  }
  if (source === 'high') {
    return candle.high;
  }
  if (source === 'low') {
    return candle.low;
  }
  if (source === 'hl2') {
    return (candle.high + candle.low) / 2;
  }
  if (source === 'hlc3') {
    return (candle.high + candle.low + candle.close) / 3;
  }
  if (source === 'ohlc4') {
    return (candle.open + candle.high + candle.low + candle.close) / 4;
  }
  return candle.close;
}

function computeSma(values: number[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  let sum = 0;

  for (let index = 0; index < values.length; index += 1) {
    sum += values[index];
    if (index >= length) {
      sum -= values[index - length];
    }
    if (index >= length - 1) {
      output[index] = sum / length;
    }
  }

  return output;
}

function computeEma(values: number[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (values.length < length) {
    return output;
  }

  let seedSum = 0;
  for (let index = 0; index < length; index += 1) {
    seedSum += values[index];
  }

  const alpha = 2 / (length + 1);
  let ema = seedSum / length;
  output[length - 1] = ema;

  for (let index = length; index < values.length; index += 1) {
    ema = ema + alpha * (values[index] - ema);
    output[index] = ema;
  }

  return output;
}

function computeSmma(values: number[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (values.length < length) {
    return output;
  }

  let seedSum = 0;
  for (let index = 0; index < length; index += 1) {
    seedSum += values[index];
  }

  let smma = seedSum / length;
  output[length - 1] = smma;

  for (let index = length; index < values.length; index += 1) {
    smma = (smma * (length - 1) + values[index]) / length;
    output[index] = smma;
  }

  return output;
}

function computeWma(values: number[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  const denominator = (length * (length + 1)) / 2;

  for (let index = length - 1; index < values.length; index += 1) {
    let weightedSum = 0;
    let weight = 1;
    for (let bar = index - length + 1; bar <= index; bar += 1) {
      weightedSum += values[bar] * weight;
      weight += 1;
    }
    output[index] = weightedSum / denominator;
  }

  return output;
}

function computeVwma(candles: Candle[], values: number[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);

  for (let index = length - 1; index < values.length; index += 1) {
    let weightedSum = 0;
    let volumeSum = 0;
    for (let bar = index - length + 1; bar <= index; bar += 1) {
      const volume = candles[bar].volume;
      weightedSum += values[bar] * volume;
      volumeSum += volume;
    }

    if (volumeSum > 0) {
      output[index] = weightedSum / volumeSum;
    }
  }

  return output;
}

function applyOffset(values: (number | undefined)[], offset: number): (number | undefined)[] {
  const shifted: (number | undefined)[] = new Array(values.length).fill(undefined);

  for (let index = 0; index < values.length; index += 1) {
    const targetIndex = index + offset;
    if (targetIndex < 0 || targetIndex >= values.length) {
      continue;
    }
    shifted[targetIndex] = values[index];
  }

  return shifted;
}

export function computeBollingerBandsOverlay({
  candles,
  length = DEFAULT_LENGTH,
  multiplier = DEFAULT_MULTIPLIER,
  source = DEFAULT_SOURCE,
  basisType = DEFAULT_BASIS_TYPE,
  offset = DEFAULT_OFFSET,
}: ComputeBollingerBandsParams): BollingerBandsOverlayResult {
  const normalizedLength = Number.isFinite(length) ? Math.max(1, Math.round(length)) : DEFAULT_LENGTH;
  const normalizedMultiplier = Number.isFinite(multiplier)
    ? Math.max(0.001, multiplier)
    : DEFAULT_MULTIPLIER;
  const normalizedOffset = Number.isFinite(offset)
    ? Math.max(-500, Math.min(500, Math.round(offset)))
    : DEFAULT_OFFSET;

  const sourceValues = candles.map((candle) => toSourceValue(candle, source));
  const basis =
    basisType === 'ema'
      ? computeEma(sourceValues, normalizedLength)
      : basisType === 'smma'
        ? computeSmma(sourceValues, normalizedLength)
        : basisType === 'wma'
          ? computeWma(sourceValues, normalizedLength)
          : basisType === 'vwma'
            ? computeVwma(candles, sourceValues, normalizedLength)
            : computeSma(sourceValues, normalizedLength);

  const upper: (number | undefined)[] = new Array(candles.length).fill(undefined);
  const lower: (number | undefined)[] = new Array(candles.length).fill(undefined);

  for (let index = normalizedLength - 1; index < candles.length; index += 1) {
    const mean = basis[index];
    if (mean === undefined) {
      continue;
    }

    let variance = 0;
    for (let bar = index - normalizedLength + 1; bar <= index; bar += 1) {
      const delta = sourceValues[bar] - mean;
      variance += delta * delta;
    }

    const stdDev = Math.sqrt(variance / normalizedLength);
    upper[index] = mean + normalizedMultiplier * stdDev;
    lower[index] = mean - normalizedMultiplier * stdDev;
  }

  return {
    upper: applyOffset(upper, normalizedOffset),
    basis: applyOffset(basis, normalizedOffset),
    lower: applyOffset(lower, normalizedOffset),
  };
}
